// Importing color configurations, styled-components library, and an image
import { colors } from "../config.js";
import styled from "styled-components";
import smile from "../images/smile.png";
import SymbolBox from "./SymbolBox.jsx";
import Ace from "../images/Pronouns compass/Svgs/Ace.jsx";

// Array of colors for the color wheel
const wheelColors = ["purple", "blue", "green", "yellow", "orange", "red"];
const intersexColors = ["purple", "yellow", "purple"];

// Ratios defining the size proportions of different wheel components
const ratios = {
  small: [0.5, 0.8],
  big: [0.4, 1],
  middle: [0.2, 0.35],
};

// Styled components for the color wheel

// Container for the entire color wheel
const WheelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: ${(props) => props.width}px;
  height: ${(props) => props.width}px;
`;

// SmallWheel represents the smaller rotating part of the color wheel
const SmallWheel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.small[1]}px;
  height: ${(props) => props.width * ratios.small[1]}px;
  transform: rotate(-30deg);
  background: conic-gradient(
    ${wheelColors
      .map(
        (color, i, all) =>
          // Generating conic-gradient color stops for the small wheel
          `${colors[color][1]} ${(360 / all.length) * i}deg, ${
            colors[color][1]
          } ${(360 / all.length) * (i + 1)}deg`
      )
      .join(", ")}
  );
`;

// SmallWheelCenter represents the center of the small wheel
const SmallWheelCenter = styled.div`
  display: block;
  position: absolute;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.small[0]}px;
  height: ${(props) => props.width * ratios.small[0]}px;
  background: ${colors.bg[0]};
`;

// BigWheel represents the larger rotating part of the color wheel
const BigWheel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(-30deg);
  background: ${(props) =>
    !props.selected.includes("intersex")
      ? `conic-gradient(${wheelColors
          .map((color, i, all) => {
            const actualColor = props.selected.some(
              (c) => c === color || c === "rainbow"
            )
              ? colors[color][0]
              : "rgba(0, 0, 0, 0)";
            return `${actualColor} ${
              (360 / all.length) * i
            }deg, ${actualColor} ${(360 / all.length) * (i + 1)}deg`;
          })
          .join(", ")})`
      : // Radial gradient for intersex
        `radial-gradient(circle, ${intersexColors
          .map((color, i, all) => {
            const stripeRatio = [2, 1, 2]; // Adjust the ratio of the stripes as needed
            const totalRatio = stripeRatio.reduce(
              (sum, ratio) => sum + ratio,
              0
            );
            let currentPosition = 0;

            const colorStops = stripeRatio.map((ratio, index) => {
              const stripeColor =
                index % 2 === 0 ? colors["purple"][0] : colors["yellow"][0];
              const startPercentage = (100 / totalRatio) * currentPosition;
              currentPosition += ratio;
              const endPercentage = (100 / totalRatio) * currentPosition;

              return `${stripeColor} ${startPercentage}%, ${stripeColor} ${endPercentage}%`;
            });

            return colorStops.join(", ");
          })

          .join(", ")})`};
`;

// purple half - right side
const BigWheelPurpleRight = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(0deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["purple"].some(
          (c) => c === color || c === "rainbow"
        )
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = 0; // You can change this value
        const endAngle = 30; // You can change this value

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;
// green half - left side
const BigWheelGreenLeft = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(-60deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["green"].some(
          (c) => c === color || c === "rainbow"
        )
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = (360 / all.length) * i;
        const endAngle = (360 / all.length) * (i + 1);

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;
// purple half - left side
const BigWheelPurpleLeft = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(-60deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["purple"].some(
          (c) => c === color || c === "rainbow"
        )
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = (360 / all.length) * i;
        const endAngle = (360 / all.length) * (i + 1) + 0.1;

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;
// orange half - left side
const BigWheelOrangeLeft = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(0deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["orange"].some(
          (c) => c === color || c === "rainbow"
        )
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = (360 / all.length) * i;
        const endAngle = (360 / all.length) * (i + 1);

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;
// orange half - right side
const BigWheelOrangeRight = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(-60deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["orange"].some(
          (c) => c === color || c === "rainbow"
        )
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = (360 / all.length) * i;
        const endAngle = (360 / all.length) * (i + 1);

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;

// green half - right side
const BigWheelGreenRight = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(0deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["green"].some(
          (c) => c === color || c === "rainbow"
        )
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = (360 / all.length) * i;
        const endAngle = (360 / all.length) * (i + 1);

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;
const BigWheelBlueLeft = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(-60deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["blue"].some((c) => c === color || c === "rainbow")
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = (360 / all.length) * i;
        const endAngle = (360 / all.length) * (i + 1);

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;

const BigWheelRedRight = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[1]}px;
  height: ${(props) => props.width * ratios.big[1]}px;
  transform: rotate(0deg);
  background: ${(props) =>
    `conic-gradient(${wheelColors
      .map((color, i, all) => {
        const actualColor = ["red"].some((c) => c === color || c === "rainbow")
          ? colors[color][0]
          : "rgba(0, 0, 0, 0)";

        // Adjust the start and end angles for smaller gradients
        const startAngle = (360 / all.length) * i;
        const endAngle = (360 / all.length) * (i + 1);

        return `${actualColor} ${startAngle}deg, ${actualColor} ${endAngle}deg`;
      })
      .join(", ")})`};
`;

// BigWheelCenter represents the center of the big wheel
const BigWheelCenter = styled.div`
  display: block;
  position: absolute;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.big[0]}px;
  height: ${(props) => props.width * ratios.big[0]}px;
  background: ${colors.bg[0]};
`;

const MiddleWheelLight = styled.div`
  display: block;
  position: absolute;
  border-radius: 50%;
  width: ${(props) => props.width * ratios.middle[0]}px;
  height: ${(props) => props.width * ratios.middle[0]}px;
  background: ${(props) =>
    props.selected && props.selected.includes("intersex")
      ? colors.bg[0]
      : colors.grey[1]};
`;

// MiddleWheelDark represents the dark-colored center of the color wheel
const MiddleWheelDark = styled.div`
  display: block;
  position: absolute;
  border-radius: 50%;
  // Using an image (smile) as a background for the dark-colored center
  background-image: url(${smile});
  background-position: center;
  background-size: cover;
  width: ${(props) => props.width * ratios.middle[1]}px;
  height: ${(props) => props.width * ratios.middle[1]}px;
`;

// ColorWheel component that combines the styled components to render the entire color wheel
// lightup is a boolean to specify whether neighbouring parts of the wheel light up or not.
const ColorWheel = ({
  selected,
  width = 270,
  isSymbol,
  neighbours,
  leftPurple,
  rightPurple,
}) => {
  // console.log(neighbours);
  return isSymbol ? (
    <WheelContainer width={width}>
      <SymbolBox selected={selected}></SymbolBox>
    </WheelContainer>
  ) : (
    <WheelContainer width={width}>
      {/* Render the SmallWheel with its center */}
      <SmallWheel width={width}>
        <SmallWheelCenter width={width} />
      </SmallWheel>

      {/* a second wheel for lightup neighbour parts */}
      {neighbours && selected.some((color) => color === "blue") && (
        <>
          {rightPurple && (
            <BigWheelPurpleRight selected={selected} width={width}>
              <BigWheelCenter width={width} />
            </BigWheelPurpleRight>
          )}

          <BigWheelGreenLeft selected={selected} width={width}>
            <BigWheelCenter width={width} />
          </BigWheelGreenLeft>
        </>
      )}
      {neighbours && selected.some((color) => color === "red") && (
        <>
          {leftPurple && (
            <BigWheelPurpleLeft selected={selected} width={width}>
              <BigWheelCenter width={width} />
            </BigWheelPurpleLeft>
          )}

          <BigWheelOrangeLeft selected={selected} width={width}>
            <BigWheelCenter width={width} />
          </BigWheelOrangeLeft>
        </>
      )}
      {neighbours && selected.some((color) => color === "yellow") && (
        <>
          <BigWheelGreenRight selected={selected} width={width}>
            <BigWheelCenter width={width} />
          </BigWheelGreenRight>
          <BigWheelOrangeRight selected={selected} width={width}>
            <BigWheelCenter width={width} />
          </BigWheelOrangeRight>
        </>
      )}
      {neighbours && selected.some((color) => color === "purple") && (
        <>
          <BigWheelBlueLeft selected={selected} width={width}>
            <BigWheelCenter width={width} />
          </BigWheelBlueLeft>
          <BigWheelRedRight selected={selected} width={width}>
            <BigWheelCenter width={width} />
          </BigWheelRedRight>
        </>
      )}
      {/* Render the BigWheel with its center */}
      <BigWheel selected={selected} width={width}>
        <BigWheelCenter width={width} />
      </BigWheel>
      {/* Render the light-colored center of the wheel */}
      <MiddleWheelLight selected={selected} width={width} />
      {/* Render the dark-colored center of the wheel if "grey" is selected */}
      {selected.some((color) => color === "grey") && (
        <svg
          viewBox={`0 0 600 600`}
          height={315}
          width={315}
          style={{ position: "absolute", zIndex: 2 }}
        >
          <Ace width="180" height="180" id="aceSmile" x={210} y={210} />
        </svg>
      )}
    </WheelContainer>
  );
};

// Export the ColorWheel component as the default export
export default ColorWheel;
